import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  fetchCustomerJourney,
  fetchCustomerJourneyOverview,
  searchCustomerJourneyUsers,
} from '@/lib/adminApi';

const SOURCE_LABEL = {
  recorded: 'Recorded',
  from_existing: 'From existing project',
};

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function subjectHref(subject) {
  if (!subject?.id) return null;
  if (subject.kind === 'story') return `/stories/${subject.id}/edit`;
  if (subject.kind === 'project') return `/narration/${subject.id}/edit`;
  if (subject.kind === 'tutorial') return `/learning/${subject.id}/editor`;
  return null;
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white/60 px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
    </div>
  );
}

export function AdminCustomerJourneyPanel() {
  const [days, setDays] = useState(7);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [queryInput, setQueryInput] = useState('');
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [journey, setJourney] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);

  const loadOverview = async (nextDays = days) => {
    setLoadingOverview(true);
    try {
      setOverview(await fetchCustomerJourneyOverview(nextDays));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load platform stats');
    } finally {
      setLoadingOverview(false);
    }
  };

  const search = async (event) => {
    event?.preventDefault();
    setSearching(true);
    try {
      const data = await searchCustomerJourneyUsers(queryInput.trim());
      setUsers(data.items || []);
      if (queryInput.trim() && !(data.items || []).length) {
        toast.info('No users matched that search');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not search users');
    } finally {
      setSearching(false);
    }
  };

  const openUser = async (userId) => {
    setLoadingJourney(true);
    try {
      setJourney(await fetchCustomerJourney(userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load this journey');
    } finally {
      setLoadingJourney(false);
    }
  };

  useEffect(() => {
    loadOverview(days);
    search();
    // Initial platform stats and the recent-user list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const counts = journey?.overview?.counts || {};
  const flowEntries = Object.entries(counts.projects_by_flow || {});
  const generationEntries = Object.entries(counts.generations_by_status || {});
  const funnel = overview?.funnel || {};

  return (
    <section className="glass-panel min-w-0 rounded-2xl p-4" data-testid="admin-customer-journey">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading flex items-center gap-2 text-lg font-semibold">
            <Users className="h-5 w-5 text-indigo-500" />
            Customer journey
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            How each person joined, what they typed, which buttons they used, and which stories
            and generations they started. Older work is included from the projects already saved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30].map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={days === value ? 'default' : 'outline'}
              className="rounded-full"
              onClick={() => setDays(value)}
            >
              {value} days
            </Button>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={() => loadOverview(days)}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {loadingOverview && !overview ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Signups" value={overview?.signups ?? 0} />
            <Stat label="Logins" value={overview?.logins ?? 0} />
            <Stat label="Active users" value={overview?.active_users ?? 0} />
            <Stat label="Projects started" value={overview?.projects_started ?? 0} />
            <Stat label="Generations" value={overview?.generations_started ?? 0} />
            <Stat label="Publish or pay" value={funnel.publish_or_purchase ?? 0} />
          </div>
          <div className="mb-4 grid gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200/80 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Funnel</p>
              <ul className="mt-2 space-y-1 text-sm">
                <li>Signed up · {funnel.signup ?? 0}</li>
                <li>Started a project · {funnel.first_project ?? 0}</li>
                <li>Started generation · {funnel.first_generation ?? 0}</li>
                <li>Published or purchased · {funnel.publish_or_purchase ?? 0}</li>
              </ul>
              {overview?.logins_by_provider && (
                <p className="mt-2 text-xs text-slate-500">
                  Logins:{' '}
                  {Object.entries(overview.logins_by_provider).map(([provider, count]) => `${provider} ${count}`).join(' · ') || 'none yet'}
                </p>
              )}
            </div>
            <div className="rounded-xl border border-slate-200/80 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top pages</p>
              <ul className="mt-2 space-y-1 text-sm">
                {(overview?.top_pages || []).length ? overview.top_pages.map((page) => (
                  <li key={page.path} className="flex justify-between gap-3">
                    <span className="truncate">{page.path}</span>
                    <span className="text-slate-500">{page.count}</span>
                  </li>
                )) : <li className="text-slate-500">Page views appear after people browse the app.</li>}
              </ul>
            </div>
          </div>
        </>
      )}

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div>
          <form className="mb-3 flex gap-2" onSubmit={search}>
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                className="h-9 pl-8"
                placeholder="Email or name"
                value={queryInput}
                onChange={(event) => setQueryInput(event.target.value)}
                data-testid="admin-customer-search"
              />
            </div>
            <Button type="submit" size="sm" disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </form>
          <ul className="max-h-[70vh] space-y-1 overflow-y-auto">
            {users.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  className={`w-full rounded-lg px-2 py-2 text-left text-sm hover:bg-slate-100/80 ${
                    journey?.user?.id === user.id ? 'bg-indigo-500/15' : ''
                  }`}
                  onClick={() => openUser(user.id)}
                >
                  <span className="block truncate font-medium">{user.name || user.email}</span>
                  <span className="block truncate text-xs text-slate-500">{user.email}</span>
                  <span className="mt-1 block text-xs text-slate-400">
                    {user.stories || 0} story studio · {user.projects || 0} projects · {user.provider}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="min-w-0">
          {loadingJourney ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : !journey ? (
            <p className="py-10 text-sm text-slate-500">Select a person to see everything they did.</p>
          ) : (
            <div data-testid="admin-customer-detail">
              <div className="mb-3">
                <h3 className="text-base font-semibold">{journey.user?.name || journey.user?.email}</h3>
                <p className="text-sm text-slate-500">
                  {journey.user?.email} · joined with {journey.overview?.provider || 'email'} on {formatWhen(journey.overview?.created_at)}
                  {journey.overview?.last_login_at ? ` · last sign-in ${formatWhen(journey.overview.last_login_at)}` : ''}
                </p>
              </div>
              <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Narration" value={counts.narration ?? 0} />
                <Stat label="All projects" value={counts.projects ?? 0} />
                <Stat label="Story studio" value={counts.stories ?? 0} />
                <Stat label="Creation plans" value={counts.plans ?? 0} />
              </div>
              <div className="mb-4 grid gap-2 sm:grid-cols-3">
                <Stat label="Publishes" value={counts.publishes ?? 0} />
                <Stat label="Credits spent" value={counts.credits_spent ?? 0} />
                <Stat label="Clicks" value={counts.clicks ?? 0} />
              </div>
              <div className="mb-4 flex flex-wrap gap-2 text-xs text-slate-600">
                {flowEntries.map(([flow, count]) => (
                  <Badge key={flow} variant="secondary">{flow} · {count}</Badge>
                ))}
                {Object.entries(counts.plans_by_flow || {}).map(([flow, count]) => (
                  <Badge key={`plan-${flow}`} variant="outline">plan {flow} · {count}</Badge>
                ))}
                {generationEntries.map(([status, count]) => (
                  <Badge key={status} variant="outline">{status} · {count}</Badge>
                ))}
                <Badge variant="outline">{counts.page_views || 0} page views</Badge>
              </div>

              <h4 className="mb-2 text-sm font-semibold">What they did</h4>
              <div className="mb-6 space-y-2">
                {(journey.overview?.work || []).length ? journey.overview.work.map((item) => (
                  <article key={`${item.kind}-${item.id}`} className="rounded-xl border border-slate-200/80 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{item.title}</p>
                      <Badge variant="secondary">{item.kind === 'plan' ? `plan · ${item.flow}` : item.kind === 'story' ? `story studio · ${item.flow}` : item.flow}</Badge>
                      <Badge variant="outline">
                        {item.generation?.ran ? `Generation ${item.generation.status}` : 'Not generated'}
                      </Badge>
                      <span className="text-xs text-slate-400">{formatWhen(item.started_at)}</span>
                    </div>
                    {item.prompt ? (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{item.prompt}</p>
                    ) : null}
                  </article>
                )) : (
                  <p className="text-sm text-slate-500">No stories or projects yet.</p>
                )}
              </div>

              <h4 className="mb-2 text-sm font-semibold">Timeline</h4>
              <ol className="space-y-3 border-l border-slate-200 pl-4">
                {(journey.timeline || []).map((item) => {
                  const href = subjectHref(item.subject);
                  return (
                    <li key={item.id} className="relative">
                      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-indigo-500" />
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium">{item.label}</p>
                        <Badge variant="outline">{SOURCE_LABEL[item.source] || item.source}</Badge>
                        {href ? (
                          <Link className="text-xs text-indigo-600 underline" to={href}>Open</Link>
                        ) : null}
                      </div>
                      <p className="text-xs text-slate-400">{formatWhen(item.at)}</p>
                      {item.detail ? <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{item.detail}</p> : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
